/**
 * Human Resources — Frontend Entry Point
 *
 * Generated from ISG App Template.
 * Uses @isg/ui-kit for shared CSS, components, and services.
 */

// --- Styles (from @isg/ui-kit) ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
// The chart's own styles are the intranet's, copied verbatim so both surfaces
// draw identically; the editor chrome is separate so re-syncing stays clean.
import './styles/org-chart.css';
import './styles/org-chart-editor.css';

// --- Services & Utils (from @isg/ui-kit barrel) ---
import { router, store, auth, checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { renderAgentWidget } from '@isg/ui-kit';

// --- i18n (registers the en-GB base catalogue at module scope) ---
import { t, initI18n } from './i18n/index.js';

/* SSO: capture the handoff from the central intranet broker.
 *
 * MUST stay at module top level, before init(). The broker returns the user to
 * /auth/sso-callback?handoff=…, and this exchanges that single-use JWT for auth
 * cookies and navigates onward.
 *
 * Omitting it does NOT produce an error — it produces a BLANK WHITE PAGE, which
 * is why this line kept getting missed. ui-kit's checkSession() deliberately
 * returns a never-resolving promise on that path so it cannot race the exchange,
 * so `await auth.checkSession()` in init() simply hangs forever. A hang is not a
 * throw, so init().catch()'s "Failed to load" fallback never fires either.
 *
 * Added to the template 2026-07-18 after isg-bi became the 15th app to hit this.
 * Every other user-facing app had already patched it individually.
 */
auth.captureSSOHandoff();

// --- Lazy-loaded pages ---
const pages = {
  adminDocuments: () => import('./pages/admin-documents.js'),
  hotTopics: () => import('./pages/hot-topics.js'),
  newStarters: () => import('./pages/new-starters.js'),
  orgChart:       () => import('./pages/org-chart.js'),
  login:          () => import('./pages/login.js'),
};

// ── Navigation config ────────────────────────────────────────────────
// This is an ADMIN app (access granted per-user via ISG Admin): the only
// surface is document management. Staff read the documents on the
// intranet's Documents page, which proxies this app's reader API.
//
// ⚠️ A FUNCTION, called at render time — never a module-level constant.
// t() at module scope runs before initI18n() has loaded the user's locale.
function navConfig() {
  return [
    {
      section: null,
      items: [
        { path: '/', label: t('nav.documents'), icon: 'file-text' },
        // ⚠️ 'sitemap' is not in ui-kit's BUILTIN_ICONS — an unknown name
        // renders NOTHING, silently. 'users' exists and is what the intranet's
        // own Directory entry uses.
        { path: '/org-chart', label: t('nav.org_chart'), icon: 'users' },
        { path: '/hot-topics', label: t('nav.hot_topics'), icon: 'megaphone' },
        { path: '/new-starters', label: t('nav.new_starters'), icon: 'user-plus' },
      ],
    },
  ];
}

// ── Render a page module into #main-content ──────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">${t('common.something_went_wrong')}</p>
          <p>${err.message || t('app.page_failed')}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">${t('app.reload')}</button>
        </div>
      </div>
    `;
  }
}

// ── App shell ────────────────────────────────────────────────────────
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Accent stripe at top
  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: t('app.title') });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: navConfig(),
    appTitle: t('app.title'),
    fallbackInitial: 'P',
    appSlug: 'hr',
  });

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(main);
  app.appendChild(layout);

}

// ── Init ─────────────────────────────────────────────────────────────
async function init() {
  // Pre-load branding so sidebar logo is ready
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  // i18n BEFORE anything renders: the login page, the shell and the nav all
  // read t(). `user.locale` is the estate-wide stored preference (null when
  // signed out, which falls back to the browser's remembered choice). A
  // failure here must not stop the app — English is always available.
  await initI18n(hasSession ? store.get('user') : null).catch(() => {});
  document.title = t('app.document_title');

  // Define routes
  router
    .on('/', (params) => renderPage(pages.adminDocuments, params))
    .on('/org-chart', (params) => renderPage(pages.orgChart, params))
    .on('/hot-topics', (params) => renderPage(pages.hotTopics, params))
    .on('/new-starters', (params) => renderPage(pages.newStarters, params))
    .on('/login', (params) => renderPage(pages.login, params));

  // Auth guard — redirects unauthenticated users to login
  router.before((path) => {
    if (path === '/login') {
      /* An AUTHENTICATED user must never be left on the login page. Returning
       * true unconditionally here (as this did until 2026-07-18) renders the
       * login FORM inside the app shell — the app appears signed in and signed
       * out simultaneously, and only a manual reload clears it.
       *
       * Not an edge case: the SSO broker returns the user to whatever `next` it
       * was carrying, and a failed exchange rewrites that to /login?sso_error=…,
       * so the first SUCCESSFUL login after any SSO hiccup lands here. Back
       * button and bookmarks reach it too. */
      if (store.get('isAuthenticated')) {
        router.navigate('/');
        return false;
      }
      return true;
    }
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  // Apply branding after every route change
  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  if (!hasSession) {
    // No session — show login
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    /* AWAIT the render. `render()` is async — it fetches /auth/config before it
     * writes any markup — so without this, applyBranding() below runs against an
     * EMPTY container: `.login-hero-logo` doesn't exist yet, the selector misses,
     * and the page keeps the placeholder LETTER instead of the ISG logo. Silent:
     * nothing errors, the branding call just finds nothing to brand. */
    await module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    // Authenticated — render app shell and resolve current route
    renderAppShell();
    checkEnvBanner(); // beta/test/in-development banner (no-op on a finished live app)
    await router.resolve();
    applyBranding();
    mountAssistant();
  }
}

/* The HR assistant.
 *
 * ⚠️ Mounted for EVERY signed-in user, and that is deliberate: the server
 * decides what they can do, not this. A viewer gets the how-to plane (no
 * tools at all, corpus only); an HR admin gets the chart tools. Hiding the
 * launcher from viewers would only hide the half of it they are entitled to.
 *
 * ⚠️ Failure is swallowed. The chart editor working without its bot is far
 * better than a bot import taking the whole app down — the same reason the
 * blueprint is registered inside a try in app/__init__.py.
 */
function mountAssistant() {
  try {
    const host = document.createElement('div');
    document.body.appendChild(host);
    renderAgentWidget(host, {
      appSlug: 'hr',
      title: t('assistant.title'),
      placeholder: t('assistant.placeholder'),
      // People's names and the group slug are passed as VALUES, never written
      // into the catalogue (HARD RULE 7: catalogues go to the translator).
      suggestions: [
        t('assistant.suggest_reports_to', { name: 'Richard Hall' }), // i18n-ignore: person name (example value)
        t('assistant.suggest_move', { name: 'Ting Yang', manager: 'Bryan Hill' }), // i18n-ignore: person names (example values)
        t('assistant.suggest_cascade'),
        t('assistant.suggest_peer_group', { group: 'uk-leadership' }), // i18n-ignore: peer-group slug (data value)
      ],
    });
  } catch (err) {
    console.error('HR assistant failed to mount:', err);
  }
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>${t('app.app_failed')}</p>
      <button class="btn btn-primary" onclick="location.reload()">${t('app.reload')}</button>
    </div>
  `;
});
